/**
 * updateAvailable — "a newer pidly is live", shared app-wide.
 *
 * UpdateNotice (mounted once in App) detects a newer deployed bundle and sets
 * the flag; every notification bell reads it through useUpdateAvailable() and
 * shows a "pidly has been updated" item with a Reload button, counted in its
 * badge. The flag only ever turns on: reloading is the one thing that clears
 * it, so it survives "Clear all".
 */
import { useSyncExternalStore } from 'react';

// THE BUNDLE a tab runs, and the one the server serves now. The entry
// script is content-hashed (`/assets/index-<hash>.js`), so the two differ
// exactly when a deploy has landed since the tab loaded. UpdateNotice polls
// them to offer Reload; lazyWithReload asks them when a chunk fails to load,
// to tell a stale tab (reload) from an outage (say so).
const BUNDLE_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;
export function runningBundle() {
  if (typeof document === 'undefined') return null;
  for (const s of document.querySelectorAll('script[src]')) {
    const m = s.getAttribute('src').match(BUNDLE_RE);
    if (m) return m[0];
  }
  return null;
}
export async function liveBundle() {
  const res = await fetch(`${window.location.origin}/?_v=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
  if (!res.ok) return null;
  const m = (await res.text()).match(BUNDLE_RE);
  return m ? m[0] : null;
}

let available = false;
const listeners = new Set();

export function setUpdateAvailable(value) {
  const next = !!value;
  if (next === available) return;
  available = next;
  listeners.forEach(fn => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** True once a newer build is live (re-renders the caller when it turns on). */
export function useUpdateAvailable() {
  return useSyncExternalStore(subscribe, () => available, () => false);
}
